import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { familyApi, errMsg, fmtDateTime } from './familyApi';
import { StatusBadge } from './StatusBadge';
import { AccessCalendar } from './AccessCalendar';
import { AccessTypePicker } from './AccessTypePicker';
import { Section, Btn, AccessSummary, ExtendControls, PasswordReset, History } from './DetailSections';
import { ProfileFields, DangerZone } from './DetailActions';

const AccessEditor = ({ a, run, busy }) => {
  const [mode, setMode] = useState(a.mode);
  const [dates, setDates] = useState(a.dates);
  useEffect(() => { setMode(a.mode); setDates(a.dates); }, [a]);
  const dirty = mode !== a.mode || dates.join() !== a.dates.join();
  return (
    <Section title="Access" testId="family-detail-access">
      <AccessSummary a={a} />
      <AccessTypePicker value={mode} onChange={setMode} testPrefix="family-detail-mode" />
      {mode === 'custom' && <AccessCalendar dates={dates} onChange={setDates} />}
      <Btn data-testid="family-detail-save-access" className="w-full" disabled={!dirty || busy}
        onClick={() => run(() => familyApi.update(a.user_id, mode === 'custom' ? { mode, dates } : { mode }), 'Access saved')}>
        Save access changes
      </Btn>
      {a.mode === 'custom' && <ExtendControls busy={busy} onExtend={(b) => run(() => familyApi.action(a.user_id, 'extend', b), 'Access extended')} />}
    </Section>
  );
};

export const AccountDetail = ({ account, onClose, onChanged, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const a = account;

  const run = async (fn, msg) => {
    setBusy(true);
    try { const updated = await fn(); onChanged(updated); toast.success(msg); } catch (e) { toast.error(errMsg(e)); throw e; } finally { setBusy(false); }
  };
  const safeRun = (fn, msg) => run(fn, msg).catch(() => {});

  return (
    <Sheet open={!!a} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" data-testid="family-detail-sheet" className="z-[10000] w-full sm:max-w-xl overflow-y-auto bg-amber-50/40 p-4 sm:p-6">
        {a && (
          <div className="space-y-4">
            <SheetHeader className="text-left">
              <SheetTitle data-testid="family-detail-name" className="text-2xl text-amber-900">{a.name}</SheetTitle>
              <SheetDescription className="text-slate-600">{a.relationship ? `${a.relationship} · ` : ''}{a.email}</SheetDescription>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <StatusBadge access={a.access} testId="family-detail-status" />
                <span data-testid="family-detail-last-login" className="text-xs text-slate-500">Last sign-in: {fmtDateTime(a.last_login)}</span>
              </div>
            </SheetHeader>
            <AccessEditor a={a} run={safeRun} busy={busy} />
            <Section title="Pause" testId="family-detail-pause-section">
              {a.access.paused
                ? <Btn tone="green" data-testid="family-detail-resume" disabled={busy} onClick={() => safeRun(() => familyApi.action(a.user_id, 'resume'), 'Access resumed')}>Resume access</Btn>
                : <Btn tone="ghost" data-testid="family-detail-pause" disabled={busy || a.access.status === 'terminated'} onClick={() => safeRun(() => familyApi.action(a.user_id, 'pause'), 'Access paused')}>Pause access now</Btn>}
              <p className="text-xs text-slate-500">Pausing blocks the protocol and phone alerts instantly. Their days are kept.</p>
            </Section>
            <ProfileFields a={a} run={safeRun} busy={busy} />
            <Section title="Reset password" testId="family-detail-password-section">
              <PasswordReset busy={busy} onReset={(pw) => run(() => familyApi.action(a.user_id, 'password', { password: pw }), 'Password updated')} />
            </Section>
            <Section title="History" testId="family-detail-history-section"><History items={a.history} /></Section>
            <DangerZone a={a} run={safeRun} busy={busy} onDeleted={onDeleted} setBusy={setBusy} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

